import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import api from '../api/axios';
import AttendanceChart from './AttendanceChart';

/**
 * AttendanceGraphView — consolidated "Attendance Timeline" for the Attendance tab.
 *
 * Reuses the exact same `AttendanceChart` component and the same `/staff/my-dashboard`
 * data as the Staff Dashboard (no duplicate chart logic). Shows every (filtered) staff
 * member's monthly timeline as an expandable row, with Expand all / Collapse all.
 *
 * Props:
 *   staffList: [{ id, name }]  — the staff to show (already filtered by the page)
 *   month:     'YYYY-MM'       — the month to render
 */
export default function AttendanceGraphView({ staffList = [], month }) {
  const [dataByStaff, setDataByStaff] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const ids = useMemo(() => staffList.map((s) => s.id), [staffList]);
  const idsKey = ids.join(',');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!ids.length || !month) { setDataByStaff({}); return; }
      setLoading(true);
      try {
        const results = await Promise.all(ids.map((id) =>
          api.get(`/staff/my-dashboard/?period=month&custom_month=${month}&staff_id=${id}`)
            .then((r) => [id, r.data])
            .catch(() => [id, null])
        ));
        if (!cancelled) {
          const map = {};
          results.forEach(([id, d]) => { map[id] = d; });
          setDataByStaff(map);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [idsKey, month]);

  // Auto-expand when a single staff member is selected.
  useEffect(() => {
    setExpanded(ids.length === 1 ? new Set(ids) : new Set());
  }, [idsKey]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setExpanded(new Set(ids));
  const collapseAll = () => setExpanded(new Set());

  if (!ids.length) {
    return <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-400">No staff to show for this filter.</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{ids.length} staff · {month}{loading ? ' · loading…' : ''}</span>
        <div className="flex gap-2">
          <button onClick={expandAll} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-1"><ChevronsUpDown size={13} /> Expand all</button>
          <button onClick={collapseAll} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-1"><ChevronsDownUp size={13} /> Collapse all</button>
        </div>
      </div>
      {staffList.map((s) => {
        const isOpen = expanded.has(s.id);
        const d = dataByStaff[s.id];
        return (
          <div key={s.id} className="border border-gray-100 rounded-lg overflow-hidden bg-white">
            <button onClick={() => toggle(s.id)} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50 text-left">
              {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              <span className="text-sm font-medium text-gray-800">{s.name}</span>
            </button>
            {isOpen && (
              <div className="p-2 border-t border-gray-100">
                {d ? <AttendanceChart data={d} calendarMonth={month} />
                   : <div className="text-xs text-gray-400 p-4">{loading ? 'Loading…' : 'No timeline data'}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
